<template>
  <div class="tl-support-documents overflow-hidden rounded-md border border-solid border-[--color-neutral-a4]">
    <div
      class="font-medium leading-6 [&_>div:first-child]:border-0 [&_>div:last-child]:border-0 [&_>div]:flex [&_>div]:justify-between [&_>div]:gap-4 [&_>div]:border-b [&_>div]:border-[--color-neutral-a4] [&_>div]:px-5 [&_>div]:py-3 [&_>div]:font-bold"
    >
      <div v-if="!displayDownloadButton" class="bg-[--color-neutral-a6] !py-[14px]">
        <h3 class="m-0 text-lg font-semibold text-[--color-neutral-a05] lg:text-xl">
          {{ $t("pages.product.documents.support_documents") }}
        </h3>
        <button
          v-if="documents.length > 0"
          type="button"
          class="flex items-center gap-1 whitespace-nowrap font-medium text-[--color-link] hover:text-[--color-neutral-a1] disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isDownloadingAll"
          @click="downloadSupportDocsAsZip"
        >
          <VcIcon name="download" :size="14" class="mt-1 self-center" />
          <span class="hide-xxs">{{ $t("pages.product.documents.download_all") }}</span>
        </button>
      </div>
      <div v-if="documents.length == 0" class="!font-medium">
        <span>{{ $t("pages.product.documents.support_documents_no_documents") }}</span>
      </div>

      <template v-for="(groupAssets, group) in groupedDocuments" :key="group">
        <div v-if="group && groupAssets.length > 1" class="tl-support-documents__group-title">{{ group }}</div>
        <div
          v-for="(document, documentIndex) in groupAssets"
          :key="document.id"
          class="tl-support-documents__group-item"
          :class="{
            'mb-2 !border-0 last:mb-0':
              documentIndex + 1 === groupAssets.length && group !== '' && groupAssets.length > 1,
            'support-doc-inner': group !== '' && groupAssets.length > 1,
          }"
        >
          <div class="overflow-wrap-anywhere flex items-center gap-2">
            <VcIcon :name="getDocumentIconName(group)" :size="16" class="self-center text-[--color-neutral-a05]" />
            {{
              groupAssets.length == 1
                ? getFileNameOrDefault(document.group)
                : getFileNameOrDefault(document.description)
            }}
          </div>
          <div class="flex flex-nowrap items-center gap-3">
            <button
              type="button"
              class="font-medium text-[--color-link] hover:text-[--color-neutral-a1] disabled:cursor-not-allowed disabled:opacity-50"
              :aria-label="getDocumentAriaLabel(group, document.name)"
              :disabled="downloadingDocuments.has(document.id)"
              @click="handleClick(document)"
            >
              {{ isPdfOrHtml(document) ? "Open" : "Download" }}
            </button>
            <button
              type="button"
              class="flex size-[26px] items-center justify-center rounded-md bg-[--color-neutral-a6] text-[--color-link] hover:text-[--color-neutral-a1]"
              :aria-label="
                copiedDocuments.has(document.id)
                  ? $t('pages.product.documents.link_copied')
                  : $t('pages.product.documents.copy_link')
              "
              @click="copyToClipboard(document.optiUrl, document.id)"
            >
              <VcIcon
                :name="copiedDocuments.has(document.id) ? 'check-circle-alt' : 'clipboard-outline'"
                :size="14"
                :class="copiedDocuments.has(document.id) ? 'text-[#48B524]' : ''"
              />
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>

  <VcButton
    v-if="displayDownloadButton"
    class="mx-5 min-w-full"
    variant="outlineTL"
    color="secondary"
    size="lg"
    :disabled="isDownloadingAll"
    @click="downloadSupportDocsAsZip"
  >
    <VcIcon name="download" :size="20" class="mr-2.5 mt-1" />
    {{ $t("pages.product.documents.download_all") }}
  </VcButton>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { useFileDownloadTracker } from "../../../gtm/download-counter";
import { getFileExtension } from "../../shared";
import { getCdnUrl } from "../../shared/image-resize";
import { downloadFile, normalizeDownloadFileName, getDocumentIconName } from "../types";
import type { Asset } from "@/core/api/graphql/types";
interface IProps {
  documents: Array<Asset>;
  productId: string;
  productCode: string;
  displayDownloadButton: boolean;
}
const props = defineProps<IProps>();

const isDownloadingAll = ref(false);
const downloadingDocuments = ref(new Set<string>());
const copiedDocuments = ref(new Set<string>());
const { gtmTrackFileDownload, isDownloadableFile } = useFileDownloadTracker();

const groupedDocuments = computed(() => {
  return props.documents.reduce(
    (acc, document) => {
      if (!acc[document.group ?? ""]) {
        acc[document.group ?? ""] = [];
      }
      acc[document.group ?? ""].push(document);
      return acc;
    },
    {} as Record<string, Asset[]>,
  );
});

const getFileNameOrDefault = (name: string | undefined) => {
  if (name) {
    return name;
  } else {
    return "File";
  }
};

const handleClick = async (document: Asset) => {
  if (downloadingDocuments.value.has(document.id)) {
    return;
  }
  downloadingDocuments.value.add(document.id);

  const isOpen = isPdfOrHtml(document);

  // MUST be synchronous for iOS Safari to take it as user initiated
  let openedWindow: Window | null = null;
  if (isOpen) {
    openedWindow = window.open("", "_blank");
  }

  try {
    const fileName = normalizeDownloadFileName(
      document.name ?? "",
      document.group ?? "",
      document.description ?? "",
      props.productCode,
      true,
    );

    if (isOpen) {
      if (isDownloadableFile(fileName, true)) {
        gtmTrackFileDownload(fileName, true);
      }

      if (openedWindow) {
        openedWindow.location.href = document.optiUrl;
        openedWindow.addEventListener("load", () => {
          URL.revokeObjectURL(document.optiUrl);
        });
      }
    } else {
      await downloadFileByUrl(document.url, fileName);
    }
  } catch (e) {
    openedWindow?.close();
    throw e;
  } finally {
    downloadingDocuments.value.delete(document.id);
  }
};

const copyToClipboard = async (text: string, documentId: string) => {
  await navigator.clipboard.writeText(text);
  copiedDocuments.value.add(documentId);
  setTimeout(() => {
    copiedDocuments.value.delete(documentId);
  }, 2000);
};

const downloadFileByUrl = async (url: string, fileName: string) => {
  const urlEncoded = encodeURIComponent(getCdnUrl(url));

  await downloadFile(`/api/thorlabs-products/download-file/${urlEncoded}`, fileName, {
    responseType: "arraybuffer",
  });
};

const downloadSupportDocsAsZip = async () => {
  if (isDownloadingAll.value) {
    return;
  }

  isDownloadingAll.value = true;

  try {
    await downloadFile(
      `/api/thorlabs-products/support-documents-zip/${props.productId}`,
      `${props.productCode}-Documents.zip`,
      {
        responseType: "arraybuffer",
      },
      { type: "application/zip" },
    );
  } finally {
    isDownloadingAll.value = false;
  }
};

const isPdfOrHtml = (document: Asset) => {
  const extension = getFileExtension(document.name)?.toLowerCase();
  return extension === "pdf" || extension === "html";
};

const getDocumentAriaLabel = (group: string, documentName: string | undefined) => {
  if (!documentName) {
    return;
  }

  const action = isPdfOrHtml({ name: documentName } as Asset) ? "Open" : "Download";
  const groupText = group ? ` as part of ${group}` : "";
  return `${action} ${documentName}${groupText}`;
};
</script>

<style lang="scss" scoped>
.support-doc-inner {
  @apply pl-8;
}
</style>
